import { create } from "zustand";

interface ProjectStore {
  // Stable slug identity (lowercase-hyphen-number). This is the storage key
  // used by every fs path, cloud key, and registry lookup.
  projectName: string | null;
  // Human-readable display name (any characters). Display-only — never used
  // to key storage. Falls back to projectName for legacy projects with no label.
  projectLabel: string | null;
  // The active project's encryption key, derived from its password at
  // unlock time. Deliberately held in memory only — never persisted to
  // localStorage/sessionStorage — so a page reload always requires the
  // password again.
  projectKey: CryptoKey | null;
  activeFileId: string | null;
  googleToken: string | null;

  setProjectKey: (key: CryptoKey | null) => void;
  /** Enter an unlocked project: persists the name+label and sets its live key. */
  enterProject: (name: string, label: string, key: CryptoKey) => void;
  setActiveFileId: (fileId: string) => void;
  setGoogleToken: (token: string) => void;
  reset: () => void;
  initialize: () => void;
}

// Get initial state from localStorage
const readStored = (key: string): string | null => {
  try {
    return localStorage.getItem(key) || null;
  } catch {
    return null;
  }
};

export const useProjectStore = create<ProjectStore>((set: any) => ({
  projectName: readStored("projectName"),
  projectLabel: readStored("projectLabel"),
  projectKey: null,
  activeFileId: null,
  googleToken: null,

  setProjectKey: (key: CryptoKey | null) => set({ projectKey: key }),
  enterProject: (name: string, label: string, key: CryptoKey) => {
    try {
      localStorage.setItem("projectName", name);
      localStorage.setItem("projectLabel", label);
    } catch {
      // Silently fail if localStorage is unavailable
    }
    set({ projectName: name, projectLabel: label, projectKey: key });
  },
  setActiveFileId: (fileId: string) => set({ activeFileId: fileId }),
  setGoogleToken: (token: string) => set({ googleToken: token }),
  reset: () => {
    try {
      localStorage.removeItem("projectName");
      localStorage.removeItem("projectLabel");
    } catch {
      // Silently fail if localStorage is unavailable
    }
    set({
      projectName: null,
      projectLabel: null,
      projectKey: null,
      activeFileId: null,
      googleToken: null,
    });
  },
  initialize: () => {
    set({
      projectName: readStored("projectName"),
      projectLabel: readStored("projectLabel"),
      projectKey: null,
    });
  },
}));
